// Coins and notes (Year 3 pictures, 21 Oct 2026). Simple drawings, not
// copies of the real designs (owner's choice): coins are plain circles in
// their real size order, the 50c with twelve sides, and notes are rounded
// cards in their real colours. The value is printed on every piece, because
// adding them up is the question; how much there is in all is never written
// or spoken.
//
// Pure parsing and geometry only, no components (see
// practice-diagram-data.ts).

export const MONEY_VALUES = [
  '5c',
  '10c',
  '20c',
  '50c',
  '$1',
  '$2',
  '$5',
  '$10',
  '$20',
  '$50',
  '$100',
] as const;

export type MoneyValue = (typeof MONEY_VALUES)[number];

export type MoneySpec = {
  type: 'money';
  items: { value: MoneyValue; count: number }[];
};

// More pieces than this do not fit a phone screen and cannot be counted.
const MAX_ITEMS = 8;
const MAX_COUNT = 10;
const MAX_PIECES = 20;

function isMoneyValue(value: unknown): value is MoneyValue {
  return (
    typeof value === 'string' &&
    (MONEY_VALUES as readonly string[]).includes(value)
  );
}

export function readMoney(record: Record<string, unknown>): MoneySpec | null {
  const { items } = record;
  if (!Array.isArray(items) || items.length < 1 || items.length > MAX_ITEMS) {
    return null;
  }
  const read: MoneySpec['items'] = [];
  let pieces = 0;
  for (const entry of items as unknown[]) {
    if (typeof entry !== 'object' || entry === null) return null;
    const { value, count } = entry as Record<string, unknown>;
    if (!isMoneyValue(value)) return null;
    if (
      typeof count !== 'number' ||
      !Number.isInteger(count) ||
      count < 1 ||
      count > MAX_COUNT
    ) {
      return null;
    }
    pieces += count;
    read.push({ value, count });
  }
  if (pieces > MAX_PIECES) return null;
  return { type: 'money', items: read };
}

export function centsOf(value: MoneyValue): number {
  return value.endsWith('c')
    ? Number(value.slice(0, -1))
    : Number(value.slice(1)) * 100;
}

export function isNote(value: MoneyValue): boolean {
  return centsOf(value) >= 500;
}

// What a screen reader is told: only whether there are coins, notes or
// both. Never the values or how many, which are the question.
export function describeMoney(money: MoneySpec): string {
  const coins = money.items.some((item) => !isNote(item.value));
  const notes = money.items.some((item) => isNote(item.value));
  if (coins && notes) return 'Australian coins and notes.';
  return coins ? 'Australian coins.' : 'Australian notes.';
}

// What follows is where everything is drawn. The units are close to one
// pixel each when the picture is shown at its natural width.

const SILVER = '#dfe3e6';
const GOLD = '#e8c766';

// Coin sizes keep the real order: 5c, $2, 10c, $1, 20c, 50c.
const COINS: Record<string, { radius: number; fill: string }> = {
  '5c': { radius: 16, fill: SILVER },
  '10c': { radius: 19, fill: SILVER },
  '20c': { radius: 23, fill: SILVER },
  '50c': { radius: 25, fill: SILVER },
  $1: { radius: 21, fill: GOLD },
  $2: { radius: 17, fill: GOLD },
};

export const NOTE_COLOURS: Record<string, string> = {
  $5: '#d7a6df',
  $10: '#8dbbe8',
  $20: '#ec9474',
  $50: '#f3d56e',
  $100: '#97d29a',
};

const NOTE = { width: 96, height: 48 };
const GAP = 10;
const EDGE = 8;
// Wide enough for three notes or about seven coins in a row.
const ROW_WIDTH = 340;

export type MoneyPiece = {
  value: MoneyValue;
  kind: 'coin' | 'note';
  // The top left corner of the box the piece fills.
  x: number;
  y: number;
  width: number;
  height: number;
  fill: string;
  // The corners of the twelve-sided 50c, ready for an SVG polygon; null
  // for a round coin or a note.
  corners: string | null;
};

export type MoneyPicture = {
  pieces: MoneyPiece[];
  width: number;
  height: number;
};

function twelveSides(cx: number, cy: number, radius: number): string {
  const points: string[] = [];
  for (let index = 0; index < 12; index += 1) {
    const turn = ((index * 30 + 15) * Math.PI) / 180;
    points.push(
      `${(cx + radius * Math.cos(turn)).toFixed(2)},${(cy + radius * Math.sin(turn)).toFixed(2)}`,
    );
  }
  return points.join(' ');
}

export function moneyPicture(money: MoneySpec): MoneyPicture {
  // Notes first, then coins, the biggest value first, like money laid out
  // on a table.
  const order = [...money.items].sort(
    (a, b) => centsOf(b.value) - centsOf(a.value),
  );
  const rows: MoneyPiece[][] = [];
  let row: MoneyPiece[] = [];
  let x = EDGE;
  for (const item of order) {
    for (let copy = 0; copy < item.count; copy += 1) {
      const note = isNote(item.value);
      const coin = COINS[item.value];
      const width = note ? NOTE.width : 2 * (coin?.radius ?? 0);
      const height = note ? NOTE.height : width;
      const last = row[row.length - 1];
      const newKind = last !== undefined && (last.kind === 'note') !== note;
      if (row.length > 0 && (newKind || x + width > ROW_WIDTH - EDGE)) {
        rows.push(row);
        row = [];
        x = EDGE;
      }
      row.push({
        value: item.value,
        kind: note ? 'note' : 'coin',
        x,
        y: 0,
        width,
        height,
        fill: note
          ? (NOTE_COLOURS[item.value] ?? SILVER)
          : (coin?.fill ?? SILVER),
        corners: null,
      });
      x += width + GAP;
    }
  }
  if (row.length > 0) rows.push(row);

  const pieces: MoneyPiece[] = [];
  let top = EDGE;
  let right = 0;
  for (const line of rows) {
    const tallest = Math.max(...line.map((piece) => piece.height));
    for (const piece of line) {
      const y = top + (tallest - piece.height) / 2;
      const radius = piece.width / 2;
      pieces.push({
        ...piece,
        y,
        corners:
          piece.value === '50c'
            ? twelveSides(piece.x + radius, y + radius, radius)
            : null,
      });
      right = Math.max(right, piece.x + piece.width);
    }
    top += tallest + GAP;
  }
  return { pieces, width: right + EDGE, height: top - GAP + EDGE };
}
